import {
  Box,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { signInWithCustomToken } from "firebase/auth";
import { TFunction } from "i18next";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { auth } from "../../auth/config";
import { useCreateUserMutation } from "../../graphql/generated/user-hooks";
import * as Sentry from "@sentry/react";
import { Button } from "../../components/chackra-ui-button";
import { useState } from "react";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { useKiosk } from "../../kiosk/kiosk-context";
import { postKioskDocument } from "../../kiosk/kiosk-documents";

const TERMS_URL = "https://clubsensei.com/condiciones-generales.pdf";
const PRIVACY_URL = "https://clubsensei.com/privacidad";

type FormValues = {
  email: string;
  password: string;
  passwordConfirmation?: string;
  acceptTerms?: boolean;
};

const validationSchema = (t: TFunction) =>
  Yup.object({
    email: Yup.string().required(t("register.emailRequired")),
    password: Yup.string()
      .required(t("register.passwordRequired"))
      .min(6, t("register.passwordMinLength")),
    passwordConfirmation: Yup.string()
      .oneOf([Yup.ref("password")], t("register.passwordsMustMatch"))
      .min(6, t("register.passwordMinLength")),
    acceptTerms: Yup.boolean().oneOf([true], t("register.mustAcceptTerms")),
  });

export const RegisterEmailForm = () => {
  const toast = useToast();
  const { isKiosk } = useKiosk();
  const { t } = useTranslation();

  const buildDocumentLinkProps = (
    kind: "pdf" | "external",
    title: string,
    url: string,
  ) =>
    isKiosk
      ? {
          onClick: (e: React.MouseEvent) => {
            e.preventDefault();
            postKioskDocument(kind, title, url);
          },
          cursor: "pointer",
        }
      : {
          href: url,
          target: "_blank",
          rel: "noopener noreferrer",
          isExternal: true,
        };

  const { isOpen, onClose, onOpen } = useDisclosure();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema(t)),
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] =
    useState(false);
  const [pendingSubmission, setPendingSubmission] = useState<{
    email: string;
    password: string;
  } | null>(null);

  const [createUser, { loading: isSubmitting }] = useCreateUserMutation({
    context: { endpoint: "fitnext" },
    onCompleted: (data) => {
      if (data?.token) {
        signInWithCustomToken(auth, data.token).catch((error) => {
          Sentry.captureException(error);
        });
      }
    },
    onError: (error) => {
      toast({ title: error.message, status: "error" });
      Sentry.captureException(error);
    },
  });

  const handleCloseConfirm = () => {
    setPendingSubmission(null);
    onClose();
  };

  const handleConfirmEmail = () => {
    if (!pendingSubmission || isSubmitting) return;
    const submission = pendingSubmission;
    setPendingSubmission(null);
    onClose();
    createUser({
      variables: {
        input: {
          email: submission.email,
          password: submission.password,
        },
      },
    }).catch((err) => Sentry.captureException(err));
  };

  const onSubmit = handleSubmit(
    ({ email, password }) => {
      const normalizedEmail = email.trim().toLowerCase();
      setPendingSubmission({ email: normalizedEmail, password });
      onOpen();
    },
    (errors, event) => {
      Sentry.captureException(new Error("Unable to submit register form"), {
        extra: { event, errors },
      });
    }
  );

  return (
    <>
      <form onSubmit={onSubmit} noValidate style={{ width: "100%" }} autoComplete="off">
        <VStack spacing={4} w="full">
          {/* Guardian notice */}
          <Box
            w="full"
            bg="slate.50"
            borderRadius="10px"
            p={4}
          >
            <Text fontSize="14px" color="slate.500" lineHeight="1.5">
              {t("register.guardianNotice")}
            </Text>
          </Box>

          <FormControl isInvalid={!!errors.email}>
            <FormLabel fontSize="16px" fontWeight={600} color="slate.600">
              {t("register.email")}
            </FormLabel>
            <Input
              id="email"
              type="email"
              autoComplete="off"
              placeholder={t("register.emailPlaceholder", "you@email.com")}
              {...register("email")}
            />
            <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
          </FormControl>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
            <FormControl isInvalid={!!errors.password}>
              <FormLabel fontSize="16px" fontWeight={600} color="slate.600">
                {t("register.password")}
              </FormLabel>
              <InputGroup>
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  pr="48px"
                  {...register("password")}
                />
                <InputRightElement h="full" pr={1}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPassword(!showPassword)}
                    minW="36px"
                    h="36px"
                    borderRadius="full"
                  >
                    <Icon boxSize={5} color="slate.400">
                      {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                    </Icon>
                  </Button>
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage>{errors.password?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.passwordConfirmation}>
              <FormLabel fontSize="16px" fontWeight={600} color="slate.600">
                {t("register.confirmPassword")}
              </FormLabel>
              <InputGroup>
                <Input
                  id="passwordConfirmation"
                  type={showPasswordConfirmation ? "text" : "password"}
                  autoComplete="new-password"
                  pr="48px"
                  {...register("passwordConfirmation")}
                />
                <InputRightElement h="full" pr={1}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setShowPasswordConfirmation(!showPasswordConfirmation)
                    }
                    minW="36px"
                    h="36px"
                    borderRadius="full"
                  >
                    <Icon boxSize={5} color="slate.400">
                      {showPasswordConfirmation ? <EyeSlashIcon /> : <EyeIcon />}
                    </Icon>
                  </Button>
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage>
                {errors.passwordConfirmation?.message}
              </FormErrorMessage>
            </FormControl>
          </SimpleGrid>

          {/* Terms checkbox in slate container */}
          <FormControl isInvalid={!!errors.acceptTerms}>
            <Box bg="slate.50" borderRadius="10px" p={4}>
              <Checkbox
                alignItems="flex-start"
                spacing={3}
                sx={{ ".chakra-checkbox__control": { mt: "2px" } }}
                {...register("acceptTerms")}
              >
                <Text fontSize="16px" textAlign="start" lineHeight="1.5" color="slate.600">
                  {t("register.acceptTerms")}{" "}
                  <Link
                    color="#0078FF"
                    fontWeight={500}
                    textDecoration="underline"
                    {...buildDocumentLinkProps(
                      "pdf",
                      t("register.termsOfService"),
                      TERMS_URL,
                    )}
                  >
                    {t("register.termsOfService")}
                  </Link>{" "}
                  {t("register.andThe")}{" "}
                  <Link
                    color="#0078FF"
                    fontWeight={500}
                    textDecoration="underline"
                    {...buildDocumentLinkProps(
                      "external",
                      t("register.privacyPolicy"),
                      PRIVACY_URL,
                    )}
                  >
                    {t("register.privacyPolicy")}
                  </Link>
                  .
                </Text>
              </Checkbox>
            </Box>
            <FormErrorMessage>{errors.acceptTerms?.message}</FormErrorMessage>
          </FormControl>

          <Button
            type="submit"
            variant="senseiFilled"
            w="full"
            isLoading={isSubmitting}
            isDisabled={isSubmitting}
            mt={2}
          >
            {t("register.createAccountButton")}
          </Button>
        </VStack>
      </form>

      <Modal isOpen={isOpen} onClose={handleCloseConfirm} isCentered size="xl">
        <ModalOverlay />
        <ModalContent borderRadius="12px" m={6}>
          <ModalHeader>
            <Text
              fontFamily="'Barlow Semi Condensed', sans-serif"
              fontWeight={600}
              fontSize="18px"
            >
              {t("register.confirmEmailModal.title")}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={3} align="start">
              <Text fontSize="16px" color="slate.600">
                {t("register.confirmEmailModal.description")}
              </Text>
              <VStack spacing={1} align="start">
                <Text fontSize="14px" color="slate.500">
                  {t("register.confirmEmailModal.label")}
                </Text>
                <Text
                  fontFamily="'Barlow Semi Condensed', sans-serif"
                  fontSize="18px"
                  fontWeight={600}
                >
                  {pendingSubmission?.email}
                </Text>
              </VStack>
            </VStack>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="senseiOutlined" onClick={handleCloseConfirm}>
              {t("register.confirmEmailModal.edit")}
            </Button>
            <Button
              variant="senseiFilled"
              onClick={handleConfirmEmail}
              isDisabled={!pendingSubmission || isSubmitting}
            >
              {t("register.confirmEmailModal.confirm")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
